/**
 * Der Packzettel — ein Auftrag an einen Subunternehmer, aus einem Angebot heraus.
 *
 * Auf dem Papier stehen Leistung, Menge und wahlweise das Budget. **Unser
 * Angebotspreis steht dort nie**, denn aus ihm ließe sich die Marge
 * zurückrechnen. Die Marge ist rein intern und steht in den Angebotsdetails.
 */
import type { WebInvoiceLine } from "./invoices";

/** Ab hier gilt eine Marge als auskömmlich — Warnschwelle, keine Sperre. */
export const MINDESTMARGE = 30;

export type PackzettelPosition = {
  id?: string;
  titel: string;
  beschreibung: string;
  menge: number;
  einheit: string;
  /** Unser Netto aus dem Angebot. Nur intern. */
  angebot_netto: number;
  /** Was der Subunternehmer bekommt. */
  budget_netto: number;
  /** Angebotsposition, aus der die Zeile stammt (Arbeitsplan). */
  position_anker?: string;
};

export type PackzettelKennzahlen = {
  angebot_netto: number;
  budget_netto: number;
  marge_netto: number;
  marge_prozent: number;
  /** Ohne Angebotswert lässt sich kein Prozentsatz bilden. */
  bewertbar: boolean;
  unter_mindestmarge: boolean;
  mindestmarge: number;
};

export type Packzettel = PackzettelKennzahlen & {
  id: string;
  nummer: string;
  entwurf_id: string;
  rechnung_id: string;
  empfaenger: string;
  betreff: string;
  hinweis: string;
  budget_zeigen: boolean;
  erstellt_von: string;
  created_at: string;
  updated_at: string;
  positionen: PackzettelPosition[];
};

export type PackzettelCreate = {
  entwurf_id?: string;
  rechnung_id?: string;
  empfaenger: string;
  betreff: string;
  hinweis: string;
  budget_zeigen: boolean;
  positionen: PackzettelPosition[];
  /** Wer führt aus und gegen welche Pauschale (Arbeitsplan, Migration 195). */
  helfer_id?: string;
  pauschale_id?: string;
};

/**
 * Dieselbe Rechnung wie im Backend (`packzettel.kennzahlen`).
 *
 * Sie steht doppelt, weil der Dialog die Marge **live beim Tippen** zeigen
 * soll — eine Serverrunde je Tastendruck wäre absurd. Maßgeblich bleibt der
 * Server; weicht etwas ab, gilt das, was dort gerechnet wurde.
 */
export function kennzahlen(positionen: PackzettelPosition[]): PackzettelKennzahlen {
  const angebot = positionen.reduce((s, p) => s + (Number(p.angebot_netto) || 0), 0);
  const budget = positionen.reduce((s, p) => s + (Number(p.budget_netto) || 0), 0);
  const marge = Math.round((angebot - budget + Number.EPSILON) * 100) / 100;
  const bewertbar = angebot > 0;
  const prozent = bewertbar ? Math.round((marge / angebot) * 1000) / 10 : 0;
  return {
    angebot_netto: Math.round((angebot + Number.EPSILON) * 100) / 100,
    budget_netto: Math.round((budget + Number.EPSILON) * 100) / 100,
    marge_netto: marge,
    marge_prozent: prozent,
    bewertbar,
    unter_mindestmarge: bewertbar && prozent < MINDESTMARGE,
    mindestmarge: MINDESTMARGE,
  };
}

/**
 * Eine Belegzeile als Packzettel-Position.
 *
 * `angebot_netto` ist Menge × Einzelpreis — der Wert, gegen den die Marge
 * gerechnet wird. Das Budget beginnt bei 0: „noch nicht beziffert", nicht
 * „kostenlos". Genau deshalb warnt der Dialog, solange nichts eingetragen ist.
 */
export function positionAusZeile(line: WebInvoiceLine): PackzettelPosition {
  return {
    titel: line.title.trim() || "Leistung",
    beschreibung: line.description.trim(),
    menge: Number(line.quantity) || 0,
    einheit: line.unit_label || "Stück",
    angebot_netto: Math.round(
      ((Number(line.quantity) || 0) * (Number(line.unit_price_net) || 0) + Number.EPSILON) * 100,
    ) / 100,
    budget_netto: 0,
  };
}
